/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export type Point = [number, number];

/**
 * Adapted from D3.js -- d3_svg_lineLinear
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @return {string} Path commands.
 */
export function lineLinear(points: Point[]) {
  return points.join('L');
}

/**
 * Adapted from D3.js -- d3_svg_lineCardinalClosed
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {number} tension A number from 0 to 1 specifying the tension.
 * @return {string} Path commands.
 */
export function lineCardinalClosed(points: Point[], tension: number) {
  // TODO: remove ts-ignores
  if (points.length < 3) {
    return lineLinear(points);
  }
  /** @ts-ignore */
  const tangents: Point[] = lineCardinalTangents(
    [points[points.length - 2]].concat(points, [points[1]]),
    tension
  );

  return (
    points[0] +
    lineHermite(
      (points.push(points[0]), points),
      points,
      /** @ts-ignore */
      tangents
    )
  );
}

/**
 * Adapted from D3.js -- d3_svg_lineCardinal
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {number} tension A number from 0 to 1 specifying the tension.
 * @return {string} Path commands.
 */
export function lineCardinal(points: Point[], tension: number) {
  return points.length < 3
    ? lineLinear(points)
    : points[0] +
        lineHermite(
          points,
          // @ts-ignore
          lineCardinalTangents(points, tension)
        );
}

/**
 * Adapted from D3.js -- d3_svg_lineHermite
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {array} tangents Tangents in the form of [[t0x t0y] [t1x t1y] ...].
 * @return {string} Path commands.
 */
export function lineHermite(points: Point[], tangents: Point[]) {
  if (
    tangents.length < 1 ||
    (points.length != tangents.length && points.length != tangents.length + 2)
  ) {
    return lineLinear(points);
  }
  const quad = points.length != tangents.length;
  let path = '';
  let p0 = points[0];
  let p = points[1];
  const t0 = tangents[0];
  let t = t0;
  let pi = 1;
  if (quad) {
    path +=
      'Q' +
      Math.round(p[0] - (t0[0] * 2) / 3) +
      ',' +
      Math.round(p[1] - (t0[1] * 2) / 3) +
      ',' +
      Math.round(p[0]) +
      ',' +
      Math.round(p[1]);
    p0 = points[1];
    pi = 2;
  }
  if (tangents.length > 1) {
    t = tangents[1];
    p = points[pi];
    pi++;
    path +=
      'C' +
      Math.round(p0[0] + t0[0]) +
      ',' +
      Math.round(p0[1] + t0[1]) +
      ',' +
      Math.round(p[0] - t[0]) +
      ',' +
      Math.round(p[1] - t[1]) +
      ',' +
      Math.round(p[0]) +
      ',' +
      Math.round(p[1]);
    for (let i = 2; i < tangents.length; i++, pi++) {
      p = points[pi];
      t = tangents[i];
      path +=
        'S' +
        Math.round(p[0] - t[0]) +
        ',' +
        Math.round(p[1] - t[1]) +
        ',' +
        Math.round(p[0]) +
        ',' +
        Math.round(p[1]);
    }
  }
  if (quad) {
    const lp = points[pi];
    path +=
      'Q' +
      Math.round(p[0] + (t[0] * 2) / 3) +
      ',' +
      Math.round(p[1] + (t[1] * 2) / 3) +
      ',' +
      Math.round(lp[0]) +
      ',' +
      Math.round(lp[1]);
  }
  return path;
}

/**
 * Adapted from D3.js -- d3_svg_lineCardinalTangents
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {number} tension A number from 0 to 1 specifying the tension.
 * @return {array} Tangents in the form of [[t0x t0y] [t1x t1y] ...].
 */
export function lineCardinalTangents(points: Point[], tension: number) {
  const tangents = [];
  const a = (1 - tension) / 2;
  let p0;
  let p1 = points[0];
  let p2 = points[1];
  let i = 1;
  const n = points.length;
  while (++i < n) {
    p0 = p1;
    p1 = p2;
    p2 = points[i];
    tangents.push([a * (p2[0] - p0[0]), a * (p2[1] - p0[1])]);
  }
  return tangents;
}

/**
 * Adapted from D3.js -- d3_svg_lineSlope
 * @param {array} p0 Point in the form of [x y].
 * @param {array} p1 Point in the form of [x y].
 * @return {number} Slope.
 */
export function lineSlope(p0: Point, p1: Point) {
  return (p1[1] - p0[1]) / (p1[0] - p0[0]);
}

/**
 * Adapted from D3.js -- d3_svg_lineFiniteDifferences
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @return {array} Finite differences.
 */
export function lineFiniteDifferences(points: Point[]) {
  let i = 0;
  const j = points.length - 1;
  const m = [];
  let p0 = points[0];
  let p1 = points[1];
  let d = (m[0] = lineSlope(p0, p1));
  while (++i < j) {
    m[i] = (d + (d = lineSlope((p0 = p1), (p1 = points[i + 1])))) / 2;
  }
  m[i] = d;
  return m;
}

/**
 * Adapted from D3.js -- d3_svg_lineMonotoneTangents
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {boolean} isHoriz If true, the spline is monotonic in X. Otherwise, it is monotonic in Y.
 * @return {array} Tangents in the form of [[t0x t0y] [t1x t1y] ...].
 */
export function lineMonotoneTangents(points: Point[], isHoriz: boolean) {
  if (isHoriz)
    // swap x and y
    points = _rotatePoints(points);

  let tangents: Point[] = [],
    d,
    a,
    b,
    s;
  const m = lineFiniteDifferences(points);
  let i = -1;
  const j = points.length - 1;
  while (++i < j) {
    d = lineSlope(points[i], points[i + 1]);
    if (Math.abs(d) < 1e-6) {
      m[i] = m[i + 1] = 0;
    } else {
      a = m[i] / d;
      b = m[i + 1] / d;
      s = a * a + b * b;
      if (s > 9) {
        s = (d * 3) / Math.sqrt(s);
        m[i] = s * a;
        m[i + 1] = s * b;
      }
    }
  }
  i = -1;
  while (++i <= j) {
    s = (points[Math.min(j, i + 1)][0] - points[Math.max(0, i - 1)][0]) / (6 * (1 + m[i] * m[i]));
    tangents.push([s || 0, m[i] * s || 0]);
  }

  if (isHoriz)
    // swap x and y again
    tangents = _rotatePoints(tangents);

  return tangents;
}

/**
 * Adapted from D3.js -- d3_svg_lineMonotone
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @param {boolean} isHoriz If true, the spline is monotonic in X. Otherwise, it is monotonic in Y.
 * @return {string} Path commands.
 */
export function lineMonotone(points: Point[], isHoriz: boolean) {
  return points.length < 3
    ? lineLinear(points)
    : points[0] + lineHermite(points, lineMonotoneTangents(points, isHoriz));
}

/**
 * Rotate the points by swapping the x and y.
 * @param {array} points Points in the form of [[p0x p0y] [p1x p1y] ...].
 * @return {array} Rotated points.
 * @private
 */
export function _rotatePoints(points: Point[]): Point[] {
  const rPoints: Point[] = [];
  for (let i = 0; i < points.length; i++) {
    rPoints.push([points[i][1], points[i][0]]);
  }
  return rPoints;
}
